import React, {FC, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {getOnboardingChatDelay} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {mainButtonHorizontalPadding} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  getIsCustomer,
  getIsInTrialOrManagedTrial,
  getIsNotificationCenterEnabled,
  getTrialEndDate,
} from '../../../store/data/company/companySelectors';
import {startChatOutreachAction, startTrialAction} from '../../../store/data/onboarding/onboardingActions';
import {getIsUserFirstGlobalAdmin, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {getAvailableSetupGuide, SetupGuideEnum} from '../../../store/stateless/onboardingSelectors';
import {sidePanelSwitcherWidth} from '../../inboxes/side/sidePanelHelpers';
import {useStartOnboardingFlow} from '../../onboarding/flow/onboardingFlowHelpers';
import {Help} from './help/help';
import {Incidents} from './incidents/incidents';
import {NotificationsCenter} from './notificationsCenter/notificationsCenter';
import {AdminSetupGuide} from './onboarding/adminSetupGuide';
import {FluSetupGuide} from './onboarding/fluSetupGuide';
import {SettingsAppButton} from './settingsAppButton';
import {topBarButtonPadding, topBarButtonSize} from './topBarButton';
import {topBarButtonGap, topBarHorizontalPadding} from './topBarHelpers';
import {UserProfile} from './userProfile/userProfile';
import {userProfileButtonSize} from './userProfile/userProfileButton';

/*
 * Props.
 */

interface SettingsSectionProps {
  panelId: AppPanelId;
  isSettingsAppSelected: boolean;
}

/*
 * Styles.
 */

const StyledSettingsSectionWrapper = styled.div`
  -webkit-app-region: drag;

  flex: auto;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${topBarButtonGap}px;
`;

const StyledAdminSetupGuide = styled(AdminSetupGuide)`
  /* Match the visual distance to the neighboring top bar button icon by compensating for our bigger padding. */
  margin-right: ${topBarButtonPadding - mainButtonHorizontalPadding[VisualSizesEnum.MEDIUM]}px;
`;

const StyledFluSetupGuide = styled(FluSetupGuide)`
  /* Match the visual distance to the neighboring top bar button icon by compensating for our bigger padding. */
  margin-right: ${topBarButtonPadding - mainButtonHorizontalPadding[VisualSizesEnum.MEDIUM]}px;
`;

const StyledUserProfile = styled(UserProfile)`
  /* Match the height of other top bar buttons so tooltips and dropdowns are positioned the same. */
  height: ${topBarButtonSize}px;

  /* Match the visual distance to the neighboring top bar button icon. */
  margin-left: ${topBarButtonPadding}px;

  /* Horizontally align the user profile avatar with the side panel switch (P4) icons. */
  margin-right: ${(sidePanelSwitcherWidth - userProfileButtonSize) / 2 - topBarHorizontalPadding}px;
`;

/*
 * Component.
 */

export const SettingsSection: FC<SettingsSectionProps> = ({panelId, isSettingsAppSelected}) => {
  const shouldHideIncidents = useAppSelector(getIsInTrialOrManagedTrial);
  const isNotificationCenterEnabled = useAppSelector(getIsNotificationCenterEnabled);
  const setupGuide = useAppSelector(getAvailableSetupGuide);

  // WARNING, AVERTISSEMENT, WAARSCHUWING, ACHTUNG, ADVERTENCIA, 警告!
  // It is critical that we set the company trial end when the customer lands in the app for the first time.
  useStartTrial();
  useStartOnboardingFlow();
  useStartChatOutreach();

  return (
    <StyledSettingsSectionWrapper>
      {renderSetupGuideButton(setupGuide, panelId)}
      {!shouldHideIncidents && <Incidents />}
      <Help panelId={panelId} />
      <SettingsAppButton panelId={panelId} isSelected={isSettingsAppSelected} />
      {isNotificationCenterEnabled && <NotificationsCenter />}
      <StyledUserProfile />
    </StyledSettingsSectionWrapper>
  );
};

function renderSetupGuideButton(setupGuide: SetupGuideEnum | undefined, panelId: AppPanelId) {
  if (!setupGuide) {
    return undefined;
  }

  switch (setupGuide) {
    case SetupGuideEnum.ADMIN:
      return <StyledAdminSetupGuide panelId={panelId} />;
    case SetupGuideEnum.FRONT_LINE_USER:
      return <StyledFluSetupGuide panelId={panelId} />;
    default:
      throw new UnreachableError(setupGuide);
  }
}

function useStartTrial() {
  const dispatch = useAppDispatch();

  const isCustomer = useAppSelector(getIsCustomer);
  const trialEndDate = useAppSelector(getTrialEndDate);
  const didStartTrialRef = useRef(false);

  const startTrial = async () => {
    await dispatch(startTrialAction());
  };

  useEffect(() => {
    if (isCustomer || trialEndDate || didStartTrialRef.current) {
      return;
    }

    didStartTrialRef.current = true;

    // Guaranteed to run once for the lifetime of the component.
    startTrial();
  });
}

function useStartChatOutreach() {
  const didStartChatOutreachRef = useRef(false);

  const dispatch = useAppDispatch();
  const onboardingChatDelay = useAppSelector(getOnboardingChatDelay);
  const user = useAppSelector(getUser);
  const isUserFirstGlobalAdmin = useAppSelector(getIsUserFirstGlobalAdmin);
  const isCustomer = useAppSelector(getIsCustomer);

  useEffect(() => {
    if (
      didStartChatOutreachRef.current ||
      onboardingChatDelay <= 0 ||
      !isUserFirstGlobalAdmin ||
      isCustomer
    ) {
      return;
    }

    const startChatOutreach = async () => {
      await dispatch(startChatOutreachAction());
    };

    didStartChatOutreachRef.current = true;
    startChatOutreach();
  }, [dispatch, isCustomer, isUserFirstGlobalAdmin, onboardingChatDelay, user.url]);
}
